import {AIAssetType} from 'common/src/modules/Assets/Assets.enums';
import {Asset} from 'common/src/modules/Assets/Assets.types';
import getActiveComp from 'common/src/modules/creativeTemplates/copilot/agents/adBuilderAgent/utils/getActiveComp';
import {creativeTemplatesApi} from 'common/src/modules/creativeTemplates/creativeTemplates.api';
import addTemplateAssetsThunk from 'common/src/modules/creativeTemplates/store/creativeTemplatePage/movieAssets/actions/addTemplateAssets';
import {TemplateAssetType} from 'common/src/modules/dynamicParameters/dynamicParameters.enums';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import {CLStore} from 'common/src/redux/store';
import getFileFromLink from 'common/src/utils/fileUtils/getFileFromLink';
import parseFileName from 'common/src/utils/fileUtils/parseFileName';
import {z} from 'zod';
import {AdBuilderAgentToolName} from '../adBuilderAgent.enums';

// Schema for importing asset from brand assets
const inputSchema = z.object({
  creativeAssetType: z
    .enum([AIAssetType.Image, AIAssetType.Video])
    .describe('Type of creative asset to import'),
  assetId: z.string().describe('ID of the brand asset to import'),
});

function importAssetFromBrandAssetsTool(store: CLStore) {
  return tool({
    name: AdBuilderAgentToolName.ImportAssetFromBrandAssets,
    displayName: 'Importing asset from brand assets',
    description:
      'Imports an asset from brand assets to template assets. Currently supports Image assets only.',
    inputSchema,
    execute: async ({input}: ToolExecuteParams<typeof inputSchema>) => {
      const state = store.getState();
      const activeComp = getActiveComp(store);

      if (!activeComp) {
        return {
          content: 'ERROR: No active composition found',
        };
      }

      const templateId = state.creativeTemplatePage.template?.id;

      if (!templateId) {
        return {
          content: 'ERROR: No template ID found in store',
        };
      }

      try {
        // Currently only support Image assets
        if (input.creativeAssetType !== AIAssetType.Image) {
          return {
            content:
              'ERROR: Only Image assets are currently supported for import from brand assets',
          };
        }

        // Get brand assets from Redux store
        const state = store.getState();
        const brandAssets = state.brandGuidelinesPage.brandAssets;

        // Check if brand assets are loaded
        if (brandAssets.images === null) {
          return {
            content: 'ERROR: Brand assets are not loaded in the store. Please load them first.',
          };
        }

        // Find the requested asset
        const foundAsset = brandAssets.images.find((asset: Asset) => asset.id === input.assetId);

        if (!foundAsset) {
          return {
            content: `ERROR: Brand asset with ID ${input.assetId} not found`,
          };
        }

        // Create filename with timestamp suffix
        const timestamp = Date.now();
        const {fileExtension: extension} = parseFileName(foundAsset.url);
        const fileName = `${foundAsset.name}_${timestamp}.${extension}`;

        // Download the asset
        const file = await getFileFromLink(foundAsset.url, fileName);

        // Create template asset
        const templateAsset = await creativeTemplatesApi.addAsset(
          templateId,
          file,
          TemplateAssetType.Image,
          undefined,
          {
            ContentDescription: foundAsset.metadata?.general?.description || foundAsset.name,
            Source: 'Brand Assets',
          },
        );

        // Update template assets using thunk
        store.dispatch(addTemplateAssetsThunk([templateAsset]));

        return {
          content: `Successfully imported .New Template asset ID: ${templateAsset.id}`,
          templateAssetId: templateAsset.id,
        };
      } catch (error) {
        return {
          content: `ERROR: Failed to import brand asset - ${error instanceof Error ? error.message : 'Unknown error'}`,
        };
      }
    },
  });
}

export default importAssetFromBrandAssetsTool;
